import axios from 'axios';

// ---------------------------------------------------------------------------
// Tempeo API client
// Thin wrapper around axios that injects auth + session headers automatically.
// ---------------------------------------------------------------------------

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL;

const http = axios.create({
  baseURL: `${BACKEND_ORIGIN}/api`,
  headers: { 'Content-Type': 'application/json' },
});

// Grab (or lazily create) a visitor session id used for analytics correlation.
const getOrCreateSessionId = () => {
  let sid = sessionStorage.getItem('session_id');
  if (!sid) {
    sid = crypto.randomUUID();
    sessionStorage.setItem('session_id', sid);
  }
  return sid;
};

// Request interceptor — attach Bearer token + session header.
http.interceptors.request.use((config) => {
  const jwt = localStorage.getItem('token');
  if (jwt) {
    config.headers.Authorization = `Bearer ${jwt}`;
  }
  config.headers['X-Session-ID'] = getOrCreateSessionId();
  return config;
});

// Response interceptor — bounce to /admin/login on 401s inside the admin area.
http.interceptors.response.use(
  (resp) => resp,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('token');
      const path = window.location.pathname;
      if (path.startsWith('/admin') && !path.includes('/login')) {
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(err);
  }
);

// Small helper to glue start/end date query params onto an existing URL.
const withDateRange = (base, startDate, endDate, sep = '?') => {
  if (!startDate) return base;
  const prefix = base.includes('?') ? '&' : sep;
  let out = `${base}${prefix}start_date=${startDate}`;
  if (endDate) out += `&end_date=${endDate}`;
  return out;
};

// -------- Auth --------
export const login = (data) => http.post('/auth/login', data);
export const register = (data) => http.post('/auth/register', data);
export const getMe = () => http.get('/auth/me');

// -------- Managers --------
export const getManagers = () => http.get('/managers');
export const createManager = (data) => http.post('/managers', data);
export const updateManager = (id, data) => http.put(`/managers/${id}`, data);
export const deleteManager = (id) => http.delete(`/managers/${id}`);

// -------- Vacancies --------
export const getVacancies = () => http.get('/vacancies');
export const createVacancy = (data) => http.post('/vacancies', data);
export const updateVacancy = (id, data) => http.put(`/vacancies/${id}`, data);
export const deleteVacancy = (id) => http.delete(`/vacancies/${id}`);

// -------- FAQ --------
export const getFAQ = () => http.get('/faq');
export const createFAQ = (data) => http.post('/faq', data);
export const updateFAQ = (id, data) => http.put(`/faq/${id}`, data);
export const deleteFAQ = (id) => http.delete(`/faq/${id}`);

// -------- Site settings --------
export const getSettings = () => http.get('/settings');
export const updateSettings = (data) => http.put('/settings', data);

// -------- Facebook pixel --------
export const getPixel = () => http.get('/pixel');
export const updatePixel = (data) => http.put('/pixel', data);

// -------- TikTok pixel --------
export const getTikTokPixel = () => http.get('/tiktok-pixel');
export const updateTikTokPixel = (data) => http.put('/tiktok-pixel', data);

// -------- Gallery --------
export const getGallery = () => http.get('/gallery');
export const createGalleryItem = (data) => http.post('/gallery', data);
export const deleteGalleryItem = (id) => http.delete(`/gallery/${id}`);

// -------- Rotation --------
export const getRotation = () => http.get('/rotation');
export const updateRotation = (data) => http.put('/rotation', data);

// -------- Tracking --------
export const trackEvent = (data) => http.post('/track/event', data);

// -------- Stats --------
export const getStatsOverview = (startDate = null, endDate = null) =>
  http.get(withDateRange('/stats/overview', startDate, endDate));

export const getTrafficStats = (days = 7) => http.get(`/stats/traffic?days=${days}`);
export const getGeoStats = () => http.get('/stats/geo');
export const getDeviceStats = () => http.get('/stats/devices');

export const getManagerStats = (startDate = null, endDate = null) =>
  http.get(withDateRange('/stats/managers', startDate, endDate));

export const getUTMStats = () => http.get('/stats/utm');
export const getEventLogs = (page = 1, limit = 50) =>
  http.get(`/stats/logs?page=${page}&limit=${limit}`);

export const getVisitorsStats = (page = 1, limit = 50, startDate = null, endDate = null) =>
  http.get(withDateRange(`/stats/visitors?page=${page}&limit=${limit}`, startDate, endDate));

// -------- Uploads --------
export const uploadFile = (file) => {
  const body = new FormData();
  body.append('file', file);
  return http.post('/upload', body, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// -------- Quiz --------
export const getQuizSettings = () => http.get('/quiz/settings');
export const updateQuizSettings = (data) => http.put('/quiz/settings', data);
export const getQuizQuestions = () => http.get('/quiz/questions');
export const createQuizQuestion = (data) => http.post('/quiz/questions', data);
export const updateQuizQuestion = (id, data) => http.put(`/quiz/questions/${id}`, data);
export const deleteQuizQuestion = (id) => http.delete(`/quiz/questions/${id}`);
export const submitQuiz = (payload) => http.post('/quiz/submit', payload);

export const getQuizResponses = (page = 1, limit = 50, startDate = null, endDate = null) =>
  http.get(withDateRange(`/quiz/responses?page=${page}&limit=${limit}`, startDate, endDate));

// -------- Public + bootstrap --------
export const getLandingData = () => http.get('/public/landing');
export const initData = () => http.post('/init');

export default http;
